/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : BarGraph.jsx
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */
/**
 *   const data = [
    ["Status", "Count", { role: "style" }, { role: "annotation" }],
    ["Draft", 0, "#D0D0CE", 0],
    ["New", 1, "#0D8390", 1],
    ["Artifact Confirmation", 2, "#007CB0", 0],
    ["Evidence Submission", 1, "#86BC25", 0],
    ["Evidence Review", 4, "#43B02A", 0],
    ["Testing at TA reviewers", 2, "#26890D", 0],
    ["Resolved", 1, "#046A38", 0],
  ];
 */
import { Box, Typography, Grid } from "@mui/material";
import React from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  LabelList,
  ResponsiveContainer,
  Cell,
} from "recharts";

const BarGraph = React.memo(({ data }) => {
  const chartData = data?.slice(1).map((item) => ({
    status: item[0],
    count: item[1],
    fill: item[2],
    annotation: item[4],
  }));

  // Custom tooltip
  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const { status, count, annotation } = payload[0].payload;
      return (
        <div
          style={{
            background: "#fff",
            border: "1px solid #ccc",
            padding: "5px",
          }}
        >
          <p>{`${status} : ${count}`}</p>
          {annotation > 0 && <p>Annotation: {annotation}</p>}
        </div>
      );
    }
    return null;
  };

  return (
    <>
      <Box
        sx={{
          width: "100%", // take full available width from GraphCard
          maxWidth: 350, // maintain consistent size
          height: 350,
          display: "flex",
          flexDirection: "column",
          alignItems: "center", // ✅ center horizontally
          justifyContent: "center", // ✅ center vertically
          mx: "auto", // ✅ center horizontally in parent (GraphCard)
          textAlign: "center", // helps with text alignment
          overflow: "hidden",
        }}
      >
        <ResponsiveContainer
          width="90%"
          height="70%"
          sx={{
            pointerEvents: "none",
            display: "flex",
            justifyContent: "center",
            padding: 0,
            margin: 0,

            userSelect: "none",
          }}
        >
          <BarChart
            data={chartData}
            activeBar={false}
            accessibilityLayer={false}
            margin={{ top: 10, right: 20, left: -15, bottom: 10 }}
            style={{
              pointerEvents: "none",
              userSelect: "none",
            }}
          >
            <CartesianGrid
              stroke="#eee"
              yAxis="viewBox"
              horizontal={true}
              vertical={false}
              style={{
                pointerEvents: "none",
                userSelect: "none",
              }}
            />
            <XAxis
              dataKey="annotation"
              axisLine={{ stroke: "#000", strokeWidth: 1 }}
              tickLine={false}
              tick={{ fontSize: "7px" }} // smaller font for X-axis too, if needed
              interval={0} // ensure all labels show
              style={{
                pointerEvents: "none",
                userSelect: "none",
              }}
            />
            <YAxis
              axisLine={false}
              tickLine={false}
              tick={{ fontSize: 10, fill: "#555" }}
              domain={[0, (dataMax) => Math.ceil(dataMax * 1.1)]}
              style={{
                pointerEvents: "none",
                userSelect: "none",
              }}
            />
            {/* <Tooltip content={<CustomTooltip />} /> */}
            <Bar
              dataKey="count"
              barSize={20}
              style={{
                pointerEvents: "none", // no hover/click
                userSelect: "none", // no selection
              }}
              tabIndex={-1}
            >
              {chartData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.fill} />
              ))}
              <LabelList
                dataKey="count"
                position="top"
                style={{ fontSize: "7px", fill: "#1f1e1eff" }}
              />
            </Bar>
          </BarChart>
        </ResponsiveContainer>

        <Box
          sx={{
            display: "flex",
            justifyContent: "center", // ✅ center the grid horizontally
            alignItems: "center",
            mt: 1,
            width: "100%",
          }}
        >
          <Grid
            container
            spacing={0.5}
            alignItems="center"
            sx={{ width: "100%", maxWidth: 300 }}
          >
            {chartData.map((item) => (
              <Grid item xs={4} key={item.status}>
                <Box
                  sx={{
                    //  display: "flex", alignItems: "center", gap: 1
                    display: "flex",
                    gap: 0.5,
                    alignItems: "center",
                    justifyContent: "flex-start",
                    textAlign: "left",
                  }}
                >
                  <Box
                    sx={{
                      width: 10,
                      height: 10,
                      bgcolor: item.fill,
                      flexShrink: 0,
                    }}
                  />
                  <Typography
                    sx={{
                      fontSize: "6px",
                      // wordBreak: "break-word", // ✅ wraps long labels
                      textAlign: "left",
                    }}
                  >
                    {item.status}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Box>
      </Box>
    </>
  );
});

export default BarGraph;
